// @flow
import { useRef, useEffect } from 'react'
import Keycloak from 'keycloak-js'

type UseKeycloakOptions = {
  config: Keycloak.KeycloakConfig,
  skip?: boolean,
  loginOptions?: Keycloak.KeycloakLoginOptions,
  initOptions?: Keycloak.KeycloakInitOptions,
  onAuthSuccess?: (keycloak: Keycloak.KeycloakInstance) => void | Promise<void>,
  onAuthRefreshSuccess?: (keycloak: Keycloak.KeycloakInstance) => void | Promise<void>,
  onAuthRefreshError?: (keycloak: Keycloak.KeycloakInstance) => void | Promise<void>,
  onAuthError?: (error: Error) => void
}

const useKeycloak = ({
  config,
  loginOptions,
  initOptions,
  skip,
  onAuthSuccess,
  onAuthRefreshSuccess,
  onAuthRefreshError,
  onAuthError
}: UseKeycloakOptions) => {
  const keycloak = useRef<Keycloak.KeycloakInstance>(new Keycloak(config))

  const handleKeycloakAuth = async () => {
    keycloak.current.onTokenExpired = () => {
      keycloak.current.updateToken(5)
    }

    keycloak.current.onAuthSuccess = () => {
      if (typeof onAuthSuccess === 'function') {
        onAuthSuccess(keycloak.current)
      }
    }

    keycloak.current.onAuthRefreshError = error => {
      if (typeof onAuthRefreshError === 'function') {
        onAuthRefreshError(error)
      }
    }

    keycloak.current.onAuthRefreshSuccess = () => {
      if (typeof onAuthRefreshSuccess === 'function') {
        onAuthRefreshSuccess(keycloak.current)
      }
    }

    try {
      const isAuthorized = await keycloak.current.init(initOptions)

      if (!isAuthorized) {
        await keycloak.current.login(loginOptions)
      }
    } catch (error) {
      if (typeof onAuthError === 'function') {
        onAuthError(error)
      }
    }
  }

  useEffect(() => {
    if (!skip) {
      handleKeycloakAuth()
    }
  }, [])

  return keycloak.current
}

export { useKeycloak }
